/**
 * Well Input Modal
 *
 * Modal dialog for entering well parameters when placing wells on the map.
 * Supports both Recovery Wells (pump GPD) and MDPE Wells (extraction CFM).
 */

import { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

export type WellType = 'recovery' | 'mdpe';

export interface WellInputModalProps {
  isOpen: boolean;
  wellType: WellType;
  onClose: () => void;
  onSave: (params: WellParams) => void;
  initialParams?: Partial<WellParams>;
}

export interface WellParams {
  pumpRateGpd?: number;     // For recovery wells
  extractionCfm?: number;   // For MDPE wells
  startMonth: number;
}

const DEFAULT_RECOVERY_GPD = 5000;  // 5,000 GPD typical residential recovery well
const DEFAULT_MDPE_CFM = 100;       // 100 CFM typical MDPE extraction rate

export function WellInputModal({
  isOpen,
  wellType,
  onClose,
  onSave,
  initialParams,
}: WellInputModalProps) {
  const [pumpRate, setPumpRate] = useState<number>(initialParams?.pumpRateGpd ?? DEFAULT_RECOVERY_GPD);
  const [extractionRate, setExtractionRate] = useState<number>(initialParams?.extractionCfm ?? DEFAULT_MDPE_CFM);
  const [startMonth, setStartMonth] = useState<number>(initialParams?.startMonth ?? 0);

  // Reset values when modal opens with new type
  useEffect(() => {
    if (isOpen) {
      setPumpRate(initialParams?.pumpRateGpd ?? DEFAULT_RECOVERY_GPD);
      setExtractionRate(initialParams?.extractionCfm ?? DEFAULT_MDPE_CFM);
      setStartMonth(initialParams?.startMonth ?? 0);
    }
  }, [isOpen, initialParams]);

  if (!isOpen) return null;

  const handleSave = () => {
    const params: WellParams = {
      startMonth,
    };

    if (wellType === 'recovery') {
      params.pumpRateGpd = pumpRate;
    } else {
      params.extractionCfm = extractionRate;
    }

    onSave(params);
    onClose();
  };

  const isRecovery = wellType === 'recovery';
  const title = isRecovery ? 'Recovery Well' : 'MDPE Well';
  const description = isRecovery
    ? 'Groundwater extraction well that creates a capture zone to intercept dissolved contamination.'
    : 'Multi-Phase Extraction well that removes vapors from the PSH source zone.';

  return (
    <div className="fixed inset-0 flex items-center justify-center" style={{ zIndex: 10000 }}>
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        style={{ zIndex: 10000 }}
      />

      {/* Modal */}
      <div className="relative bg-mono-900 border border-mono-700 rounded-lg shadow-xl w-full max-w-md mx-4" style={{ zIndex: 10001 }}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-mono-800">
          <div className="flex items-center gap-3">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center ${
                isRecovery ? 'bg-mono-700' : 'bg-mono-800'
              }`}
            >
              <div
                className={`w-4 h-4 rounded-full ${
                  isRecovery ? 'bg-mono-400' : 'bg-mono-500'
                }`}
              />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-mono-100">{title}</h3>
              <p className="text-[10px] text-mono-500 uppercase tracking-wider">
                {isRecovery ? 'Dissolved Plume Capture' : 'PSH Vapor Extraction'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-mono-500 hover:text-mono-300 transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-4 py-4 space-y-4">
          <p className="text-xs text-mono-400">{description}</p>

          {/* Rate Input */}
          <div>
            <label className="block text-xs font-medium text-mono-300 mb-1.5">
              {isRecovery ? 'Pump Rate (GPD)' : 'Extraction Rate (CFM)'}
            </label>
            <div className="relative">
              <input
                type="number"
                min={0}
                value={isRecovery ? pumpRate : extractionRate}
                onChange={(e) => {
                  const val = parseFloat(e.target.value) || 0;
                  if (isRecovery) {
                    setPumpRate(val);
                  } else {
                    setExtractionRate(val);
                  }
                }}
                className="w-full bg-mono-800 border border-mono-700 rounded px-3 py-2 text-sm text-mono-100 focus:outline-none focus:ring-2 focus:ring-mono-500/50 focus:border-mono-500"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-mono-500">
                {isRecovery ? 'gallons/day' : 'ft³/min'}
              </span>
            </div>
            <p className="text-[10px] text-mono-500 mt-1">
              {isRecovery
                ? 'Typical range: 1,000 - 50,000 GPD'
                : 'Typical range: 50 - 500 CFM'}
            </p>
          </div>

          {/* Start Month Slider */}
          <div>
            <label className="block text-xs font-medium text-mono-300 mb-1.5">
              Start Month: {startMonth === 0 ? 'Immediately' : `Month ${startMonth}`}
            </label>
            <input
              type="range"
              min={0}
              max={60}
              value={startMonth}
              onChange={(e) => setStartMonth(parseInt(e.target.value))}
              className="w-full h-2 bg-mono-800 rounded-lg appearance-none cursor-pointer accent-mono-400"
            />
            <div className="flex justify-between text-[10px] text-mono-500 mt-1">
              <span>Start now</span>
              <span>60 months</span>
            </div>
          </div>

          {/* Physics Explanation */}
          <div className="rounded-lg p-3 bg-mono-800/50">
            <h4 className="text-xs font-medium mb-1 text-mono-300">
              How It Works
            </h4>
            <p className="text-[10px] text-mono-400">
              {isRecovery ? (
                <>
                  Creates a <strong>capture zone</strong> that intercepts contaminated groundwater
                  flowing downgradient. Higher pump rates create wider capture zones.
                  The "vortex" effect changes the flow gradient from expansion to convergence.
                </>
              ) : (
                <>
                  Applies <strong>vacuum extraction</strong> to the vadose zone, pulling vapors
                  from the PSH source. This accelerates natural volatilization, depleting the
                  source faster and reducing dissolved plume feeding.
                </>
              )}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-mono-800">
          <button
            onClick={onClose}
            className="px-4 py-1.5 text-xs font-medium text-mono-400 hover:text-mono-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-1.5 text-xs font-medium rounded transition-colors bg-mono-100 hover:bg-white text-mono-900"
          >
            Place Well
          </button>
        </div>
      </div>
    </div>
  );
}

export default WellInputModal;
